import { useEffect, useState } from "react";
import { useTheme } from "./useTheme";

/**
 * Chart libraries (Chart.js / recharts) paint to canvas and won't parse CSS
 * custom-property strings. Use this hook to read the resolved color values at
 * render time so chart palettes follow the active theme.
 *
 * Usage:
 *   const c = useChartTokens(["primary", "primaryText", "baseWhite09"]);
 *   <Chart ... borderColor={c.primary} backgroundColor={c.primaryText} />
 */

const TOKEN_PREFIX = "--app-";

const toCssVarName = (token: string): string => {
	// Convert camelCase token name to the kebab-case CSS variable suffix.
	// Insert a dash before each uppercase letter AND before the first digit
	// that follows a lowercase letter — so primaryText05 -> primary-text-05
	// (matches --app-primary-text-05 in _variables.scss).
	const kebab = token
		.replace(/([A-Z])/g, "-$1")
		.replace(/([a-z])(\d)/g, "$1-$2")
		.toLowerCase();
	return `${TOKEN_PREFIX}${kebab}`;
};

const readVar = (cssVar: string): string => {
	if (typeof document === "undefined") return "";
	const value = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
	return value;
};

export const useChartTokens = <T extends string>(tokens: readonly T[]): Record<T, string> => {
	const { mode } = useTheme();
	const [values, setValues] = useState<Record<T, string>>(() => {
		const initial = {} as Record<T, string>;
		for (const t of tokens) initial[t] = readVar(toCssVarName(t));
		return initial;
	});

	useEffect(() => {
		const next = {} as Record<T, string>;
		for (const t of tokens) next[t] = readVar(toCssVarName(t));
		setValues(next);
		// `mode` change re-runs the effect so chart consumers re-resolve.
		// `tokens` is expected to be a stable identifier (defined at module level).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode]);

	return values;
};

/**
 * Read a single token's resolved value, theme-aware. Convenience wrapper.
 */
export const useChartToken = (token: string): string => {
	const result = useChartTokens([token]);
	return result[token];
};
